import { colorOf, indexToSquare, parsePlacement } from '@chessmate/game-engine';
import type { PieceChar } from '@chessmate/protocol';

export interface ScenePiece {
  readonly square: string;
  readonly char: PieceChar;
  readonly color: 'w' | 'b';
}

/** FEN → ordered piece list for the 3D scene. Pure, deterministic. */
export function fenToPieces(fen: string): ScenePiece[] {
  const placement = parsePlacement(fen);
  const out: ScenePiece[] = [];
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null) continue;
    out.push({
      square: indexToSquare(i),
      char,
      color: colorOf(char),
    });
  }
  return out;
}

/**
 * Square → world position on an 8x8 board spanning [-3.5, 3.5]. a1 at
 * (3.5, 0, -3.5).
 *
 * The camera sits on the -z side looking toward +z, so SCREEN-RIGHT is world -x.
 * For the a-file to land on the player's left (as on every printed diagram, and
 * as the DOM tap grid assumes), it has to be at +x. Rank 1 is the near edge
 * (z = -3.5), rank 8 the far edge. This used to be `file - 3.5`, which drew the
 * whole board mirrored (h on the left) against a tap grid that read a..h.
 */
export function squareToVec3(square: string): [number, number, number] {
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]);
  const x = 3.5 - file;
  const z = 3.5 - (8 - rank);
  return [x, 0, z];
}

/** Inverse of squareToVec3: world (x, z) → square. Off-board positions clamp to the edge. */
export function squareFromPos(x: number, z: number): string {
  const file = Math.min(7, Math.max(0, Math.round(3.5 - x)));
  const rank = Math.min(8, Math.max(1, Math.round(z + 4.5)));
  return `${String.fromCharCode(97 + file)}${rank}`;
}
